import { useId, useState } from 'react';
import { useLicense } from './context';
import { track } from '../analytics/client';

interface LicenseKeyFormProps {
  onActivated?: () => void;
  /** Stable id for the key input so upgrade prompts can direct focus to it. */
  inputId?: string;
}

/**
 * License-key entry form, rendered by the {@link LicensePanel} (the single
 * activation point — inline upgrade prompts link here rather than duplicating
 * the form). On a valid key it clears itself and notifies via `onActivated`;
 * on an invalid key it surfaces an inline error and leaves the free tier
 * untouched.
 */
export function LicenseKeyForm({ onActivated, inputId: stableId }: LicenseKeyFormProps) {
  const { activate } = useLicense();
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const generatedId = useId();
  const inputId = stableId ?? generatedId;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      // Real keys are validated remotely (async); stub / DEV keys resolve at once.
      const result = await activate(key);
      if (result.valid) {
        track('license_activate', { outcome: 'ok' });
        setKey('');
        onActivated?.();
      } else {
        track('license_activate', { outcome: 'invalid' });
        // Give a rejected key a recovery path (cue-forge-w28.16): a real buyer's
        // key is in their Lemon Squeezy purchase email / order page, not lost.
        setError(
          'That license key is not valid — you can find the right one in your Lemon Squeezy receipt email or on your order confirmation page.',
        );
      }
    } catch {
      track('license_activate', { outcome: 'error' });
      // activateRemote only throws when the remote check itself failed (fetch
      // error / non-OK response) — a rejected key resolves with valid:false
      // above. Say so plainly: offline-first users must not read a network
      // failure as "your key is bad" (cue-forge-drt.19).
      setError(
        "Couldn't reach the license server — check your connection. Your key wasn't rejected.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="cf-license-form" onSubmit={(e) => void onSubmit(e)}>
      <label className="cf-field" htmlFor={inputId}>
        <span>License key</span>
        <input
          id={inputId}
          className="cf-input cf-mono"
          value={key}
          placeholder="CFRG-XXXX-XXXX-XXXX"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setKey(e.target.value)}
        />
      </label>
      <button type="submit" className="cf-btn cf-btn--primary" disabled={pending}>
        {pending ? 'Activating…' : 'Activate'}
      </button>
      {error && (
        <p className="cf-license-form__error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
